import LegalShell, { LegalSection } from "../components/LegalShell";

export default function AccessibilityStatement() {
  return (
    <LegalShell
      title="Accessibility Statement"
      subtitle="Effective September 1, 2026"
      description="Mitaja Corporation's commitment to accessible digital experiences, including alignment with Section 508 and WCAG standards."
      lastUpdated="September 1, 2026"
      documentLabel="Mitaja Corporation — Accessibility Statement"
    >
      <LegalSection title="Our Commitment">
        <p>
          Mitaja Corporation is committed to providing a website that is accessible to the widest possible audience,
          including people with disabilities, and to making our digital content usable by everyone. We aim to design, build,
          and maintain this website with accessibility as a core consideration.
        </p>
      </LegalSection>

      <LegalSection title="Accessibility Target">
        <p>
          This website is designed to support and align with the accessibility requirements of the U.S. Section 508
          standards, as revised (36 C.F.R. Part 1194), which incorporate the Web Content Accessibility Guidelines
          (WCAG) 2.0 Level AA published by the World Wide Web Consortium (W3C). We use WCAG 2.2 Level AA as our broader
          design target, applying its more current success criteria across the site wherever feasible.
        </p>
        <p>
          We have not completed a formal accessibility conformance review, and we do not represent this website as
          "Section 508 certified," "fully Section 508 compliant," or "fully WCAG conformant." Formal validation, including
          documented testing with assistive technologies by a qualified reviewer, must be completed before any such
          claim is made.
        </p>
      </LegalSection>

      <LegalSection title="Accessibility Features Implemented">
        <p>
          This website has been designed with the following accessibility considerations:
        </p>
        <ul className="list-disc list-inside space-y-1.5">
          <li>A "Skip to main content" link to bypass repeated navigation</li>
          <li>Semantic page structure with clear headings, lists, and landmarks</li>
          <li>Keyboard-accessible navigation, dropdown menus, controls, and mobile navigation</li>
          <li>Visible keyboard-focus indicators and logical focus order</li>
          <li>Meaningful alternative text for informative images and empty alt text for decorative images</li>
          <li>Descriptive link text, including labels that indicate PDF downloads</li>
          <li>Labels programmatically associated with form inputs, accessible required-field markers, and live form status messages</li>
          <li>Text and background color combinations designed to support WCAG AA contrast</li>
          <li>Support for screen readers and browser zoom without clipping or loss of content</li>
          <li>Respect for user preferences regarding reduced motion</li>
        </ul>
      </LegalSection>

      <LegalSection title="Multimedia and Documents">
        <p>
          Any audio or video content we publish is provided with captions, transcripts, or text alternatives where
          present. Downloadable documents, including the Mitaja Corporation Capability Statement PDF, are accompanied by
          an accessible HTML alternative so the information remains available to users who rely on assistive technology.
          We aim to publish PDFs with meaningful document titles, language, reading order, headings, tags, link text, and
          image descriptions, and we continue to review our published documents against these criteria.
        </p>
      </LegalSection>

      <LegalSection title="Known Limitations">
        <p>
          We continue to test and improve this website. Some content, including documents and images published by third
          parties, may not yet fully meet all WCAG 2.2 Level AA or Section 508 criteria. Accessibility should be assessed
          across a range of browsers, assistive technologies, and device configurations. Where we identify barriers, we
          work to address them.
        </p>
      </LegalSection>

      <LegalSection title="Reporting an Accessibility Barrier">
        <p>
          We welcome feedback on the accessibility of this website. If you encounter a barrier, please report it to us. To
          help us understand and resolve the issue, please describe:
        </p>
        <ul className="list-disc list-inside space-y-1.5">
          <li>The page or feature where you experienced the problem</li>
          <li>The specific issue or barrier you encountered</li>
          <li>The web browser and operating system you were using</li>
          <li>The assistive technology you were using, if any</li>
        </ul>
      </LegalSection>

      <LegalSection title="Contact Us">
        <p>
          Please direct accessibility feedback and reports to:
        </p>
        <p className="font-semibold text-foreground-900">
          <a
            href="mailto:governmentsales@mitajacorp.com"
            className="text-primary-700 hover:text-primary-600 underline"
          >
            governmentsales@mitajacorp.com
          </a>
        </p>
        <p>
          We will respond to accessibility feedback and work to correct reported barriers where feasible.
        </p>
      </LegalSection>
    </LegalShell>
  );
}